import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { authAPI, setToken, getToken, removeToken } from '../services/api';

const AuthContext = createContext(null);
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const generation = useRef(0);
  const saveUser = useCallback((profile) => {
    if (!profile?.id) throw new Error('No se pudo validar el perfil de la cuenta.');
    localStorage.setItem('upim_user', JSON.stringify(profile));
    setUser(profile);
    return profile;
  }, []);
  const clearSession = useCallback(() => {
    removeToken();
    localStorage.removeItem('upim_user');
    setUser(null);
  }, []);
  const logout = useCallback(() => {
    generation.current += 1;
    clearSession();
    setLoading(false);
  }, [clearSession]);

  useEffect(() => {
    const version = ++generation.current;
    const token = getToken();
    if (!token) {
      clearSession();
      setLoading(false);
    } else {
      // Cached profile data never grants a session, even when it is valid JSON.
      authAPI.getProfile().then(profile => {
        if (version === generation.current && token === getToken()) saveUser(profile);
      }).catch(() => {
        if (version === generation.current && token === getToken()) clearSession();
      }).finally(() => {
        if (version === generation.current) setLoading(false);
      });
    }
    return () => { generation.current += 1; };
  }, [clearSession, saveUser]);

  const requireCurrent = (version) => {
    if (version !== generation.current) throw new Error('La sesión cambió. Intenta de nuevo.');
  };
  const acceptSession = (response, version, previousUser) => {
    requireCurrent(version);
    const profile = response.user || previousUser;
    if (!response.access_token || !profile?.id) throw new Error('Respuesta de acceso incompleta.');
    const current = { ...profile };
    for (const field of ['must_change_password', 'totp_enabled']) {
      if (typeof response[field] === 'boolean') current[field] = response[field];
    }
    setToken(response.access_token);
    return saveUser(current);
  };
  const loginWithEmail = async (email, password) => {
    const version = ++generation.current;
    clearSession();
    setLoading(true);
    try {
      const response = await authAPI.login(email, password);
      requireCurrent(version);
      if (response.totp_required) return { totpRequired: true, pendingToken: response.pending_token };
      const profile = acceptSession(response, version);
      return { user: profile, mustChangePassword: !!profile.must_change_password };
    } finally {
      if (version === generation.current) setLoading(false);
    }
  };
  const verifyTotpLogin = async (pendingToken, code, backupCode, attempt) => {
    if (attempt?.cancelled) throw new Error('El intento de acceso fue cancelado.');
    const version = ++generation.current;
    if (attempt) attempt.generation = version;
    clearSession();
    setLoading(true);
    try {
      const response = await authAPI.verifyTotpLogin(pendingToken, code, backupCode);
      const profile = acceptSession(response, version);
      return { user: profile, mustChangePassword: !!profile.must_change_password };
    } finally {
      if (version === generation.current) setLoading(false);
    }
  };
  const register = async (data) => {
    const version = ++generation.current;
    clearSession();
    setLoading(true);
    try { return acceptSession(await authAPI.register(data), version); }
    finally { if (version === generation.current) setLoading(false); }
  };
  const refreshProfile = useCallback(async () => {
    const version = ++generation.current;
    const token = getToken();
    if (!token) { logout(); throw new Error('Inicia sesión para continuar.'); }
    // A callback may have replaced the token while another identity was cached.
    setUser(null);
    localStorage.removeItem('upim_user');
    setLoading(true);
    try {
      const profile = await authAPI.getProfile();
      if (version !== generation.current || token !== getToken()) throw new Error('La sesión cambió.');
      return saveUser(profile);
    } catch (error) {
      if (version === generation.current && [401, 403].includes(error.status)) logout();
      throw error;
    } finally { if (version === generation.current) setLoading(false); }
  }, [logout, saveUser]);
  const isAuthenticationCurrent = useCallback((attempt) => (
    !!attempt && !attempt.cancelled && attempt.generation === generation.current
  ), []);

  const cancelPendingAuthentication = useCallback((attempt) => {
    if (!attempt) return;
    attempt.cancelled = true;
    // A departing callback must never log out a newer, unrelated identity.
    if (attempt.generation !== generation.current) return;
    generation.current += 1;
    clearSession();
    setLoading(false);
  }, [clearSession]);

  const exchangeGoogleSession = useCallback(async (sessionId, attempt) => {
    if (attempt?.cancelled) throw new Error('El intento de acceso fue cancelado.');
    const version = ++generation.current;
    if (attempt) attempt.generation = version;
    clearSession();
    setLoading(true);
    try {
      const response = await fetch(
        (process.env.REACT_APP_BACKEND_URL || '') + '/api/auth/google/session',
        { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_id: sessionId }) }
      );
      if (!response.ok) throw new Error('No se pudo procesar la sesión de Google.');
      const data = await response.json();
      requireCurrent(version);
      if (data?.totp_required === true) {
        if (typeof data.pending_token !== 'string' || !data.pending_token || data.access_token) {
          throw new Error('Desafío de segundo factor incompleto.');
        }
        return { totpRequired: true, pendingToken: data.pending_token, message: data.message };
      }
      if (typeof data?.access_token !== 'string' || !data.access_token) {
        throw new Error('Respuesta de acceso incompleta.');
      }
      setToken(data.access_token);
      const profile = await authAPI.getProfile();
      requireCurrent(version);
      if (getToken() !== data.access_token) throw new Error('La sesión cambió.');
      if (data.user_id != null && String(profile?.id) !== String(data.user_id)) {
        throw new Error('La identidad de la sesión no coincide.');
      }
      // /auth/me is newer and canonical, including password and TOTP flags.
      const current = saveUser(profile);
      return { user: current, mustChangePassword: !!current.must_change_password };
    } catch (error) {
      if (version === generation.current) clearSession();
      throw error;
    } finally {
      if (version === generation.current) setLoading(false);
    }
  }, [clearSession, saveUser]);
  const changePasswordRequired = async (oldPassword, newPassword) => {
    const version = ++generation.current;
    const response = await authAPI.changePasswordRequired(oldPassword, newPassword);
    acceptSession(response, version, user);
    return response;
  };
  const enableTotp = async (code) => {
    const version = generation.current;
    const response = await authAPI.totpEnable(code);
    requireCurrent(version);
    if (response.totp_enabled !== true) throw new Error('No se pudo confirmar la activación de 2FA.');
    if (Object.prototype.hasOwnProperty.call(response, 'access_token')) {
      if (typeof response.access_token !== 'string' || !response.access_token) {
        throw new Error('No se pudo validar la nueva sesión de seguridad.');
      }
      // The new backend rotates prior sessions after the factor is confirmed.
      acceptSession(response, version, user);
    } else {
      // Compatibility with the existing backend during frontend-first rollout.
      saveUser({ ...user, totp_enabled: true });
    }
    return response;
  };
  const updateProfile = async (updates) => {
    const version = generation.current;
    const updated = await authAPI.updateProfile(updates);
    requireCurrent(version);
    return saveUser({ ...user, ...updated });
  };
  const loginWithGoogle = async () => { throw new Error('Google login no disponible aún'); };
  return <AuthContext.Provider value={{ user, loading, isAuthenticated: !!user,
    loginWithEmail, verifyTotpLogin, loginWithGoogle, register, logout, updateProfile,
    refreshProfile, exchangeGoogleSession, cancelPendingAuthentication, isAuthenticationCurrent, changePasswordRequired, enableTotp }}>{children}</AuthContext.Provider>;
};
export default AuthContext;
